/**
 * Форматирование денежных сумм.
 *
 * «1620010 ₽» без группировки разрядов нечитаемо и на узком экране переносится посреди числа
 * (аудит 16.09, F12). Разряды разделяются узким неразрывным пробелом — он и группирует, и не
 * даёт строке сломаться по середине суммы.
 *
 * От миллиона сумма показывается компактно («1,6 млн ₽»), а точное значение уходит в `title`:
 * на KPI-карточке важен порядок величины, точность доступна по наведению.
 */

/** Символ рубля — один на приложение, чтобы не расходился по файлам. */
export const RUB = '₽';

/** Узкий неразрывный пробел: группирует разряды и не даёт перенести число посередине. */
const THIN_NBSP = ' ';

/** С этой суммы показывается компактная форма. */
export const COMPACT_FROM = 1_000_000;

/** Что показать вместо суммы, когда её нет: неизвестное — не ноль. */
export const MONEY_UNKNOWN = '—';

/** Целое число с разделителями разрядов: `1 620 010`. */
export function formatAmount(value: number): string {
    const rounded = Math.round(value);
    const sign = rounded < 0 ? '-' : '';
    const digits = Math.abs(rounded).toString();
    let grouped = '';
    for (let i = 0; i < digits.length; i++) {
        const fromEnd = digits.length - i;
        grouped += digits[i];
        if (fromEnd > 1 && fromEnd % 3 === 1) {
            grouped += THIN_NBSP;
        }
    }
    return sign + grouped;
}

/** Полная сумма с разрядами и знаком валюты: `1 620 010 ₽`. */
export function formatMoney(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
        return MONEY_UNKNOWN;
    }
    return `${formatAmount(value)}${THIN_NBSP}${RUB}`;
}

/**
 * Компактная сумма от миллиона: `1,6 млн ₽`. Меньше миллиона — обычная форма с разрядами,
 * потому что «0,8 млн» читается хуже, чем «820 400».
 */
export function formatMoneyCompact(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
        return MONEY_UNKNOWN;
    }
    const abs = Math.abs(value);
    if (abs < COMPACT_FROM) {
        return formatMoney(value);
    }
    const millions = value / COMPACT_FROM;
    const text = millions.toFixed(millions >= 100 ? 0 : 1).replace('.', ',');
    return `${text}${THIN_NBSP}млн${THIN_NBSP}${RUB}`;
}

/**
 * Пара «что показать» и «что положить в `title`»: компактная форма всегда сопровождается
 * полной суммой, иначе точное значение становится недоступным.
 */
export function money(value: number | null | undefined): {text: string; title?: string} {
    const text = formatMoneyCompact(value);
    const full = formatMoney(value);
    return text === full ? {text} : {text, title: full};
}
